/**
 * CmsImage — the one way a public page renders a media-library image.
 *
 * WHY A SHARED COMPONENT (R4, 2026-10-30)
 * ---------------------------------------
 * Before this, ~30 hand-written <img> tags each picked a variant by hand:
 * most asked for the 1024px `medium` rung, and a 375px phone downloaded all
 * of it. Not one of them carried a `srcset`, and the Markdown body images
 * carried no `width`/`height` at all, so every body image shifted the page
 * as it decoded.
 *
 * This component resolves all of that from the media index ALONE:
 *
 *   src      the largest WebP rung available — NEVER the original, which can
 *            be several megabytes and which no layout on this site needs
 *   srcSet   every rung with a KNOWN true width, ascending (see
 *            src/utils/cmsMediaUrl.ts)
 *   sizes    the layout width hint (default '100vw')
 *   width    the row's native width, so the browser reserves the box before
 *   height   the image decodes (no layout shift)
 *   alt      the prop, else the media library's alt for the CURRENT locale
 *   loading  'lazy' by default; 'eager' + fetchPriority for above-the-fold
 *   decoding 'async' by default
 *
 * If the row has no WebP rung at all (a PDF, a video) the component renders
 * NOTHING — an <img> pointing at a PDF is a broken image, and a broken image
 * with an alt is worse than no image. Callers that have their own fallback
 * (an icon tile, a placeholder) render it themselves.
 *
 * `sizes` — WHY THE DEFAULT IS '100vw'
 * -----------------------------------
 * `sizes` tells the browser how wide the image will be RENDERED, and the
 * browser picks the smallest candidate that still covers that width times the
 * device pixel ratio. A `sizes` that is too SMALL makes the browser pick a
 * file that is too small and then upscale it — visibly worse than the single
 * 1024px file this release replaces. A `sizes` that is too LARGE only wastes
 * a few kilobytes.
 *
 * So the default is the safe direction: '100vw' is the largest an image on
 * this site can be, and it can never cause an upscale. Override it ONLY where
 * the layout width is genuinely known from the CSS being read (a 3-up grid
 * column, a fixed-width thumbnail), and never with a guess. When in doubt,
 * leave the default.
 */
import type { ImgHTMLAttributes } from 'react'
import { useTranslation } from 'react-i18next'
import { cmsMediaLargestWebpUrl, cmsMediaSrcSet } from '@/utils/cmsMediaUrl'
import { mediaAltForLocale } from '@/cms/mediaAlt'
import { useCmsMediaIndex } from '@/hooks/useCmsMediaIndex'

export interface CmsImageProps
  extends Omit<
    ImgHTMLAttributes<HTMLImageElement>,
    'src' | 'srcSet' | 'sizes' | 'alt' | 'width' | 'height'
  > {
  /**
   * The `cms_media.uuid` of the image.
   *
   * Nothing else identifies the image: the component looks the row up in the
   * generated media index and takes the url, the widths, the native size and
   * the alt from there. An unknown uuid renders nothing.
   */
  uuid: string | null | undefined
  /**
   * The accessible name. When omitted, the media library's alt for the
   * current locale is used (locale → the other script → English; an empty
   * column counts as "not filled in" and is skipped).
   *
   * Pass an explicit alt when the page has a better name for the picture
   * than the library does — an article's title, a product's name.
   */
  alt?: string
  /**
   * The layout width hint. See the header for why the default is '100vw'
   * and when it is safe to override.
   */
  sizes?: string
  /** Overrides the row's native width (the intrinsic size is the default). */
  width?: number
  /** Overrides the row's native height (the intrinsic size is the default). */
  height?: number
  /**
   * Above-the-fold image: eager loading + a high fetch priority.
   *
   * Use it for at most the one image that IS the LCP element of the page.
   * Everything else stays lazy — a lazy image that is already in view is a
   * wasted round trip, and an eager image that is below the fold steals
   * bandwidth from the one that matters.
   */
  priority?: boolean
  /**
   * Overrides the `loading` attribute. 'lazy' by default; 'eager' for an
   * above-the-fold image.
   */
  loading?: 'eager' | 'lazy'
  /** Overrides the `decoding` attribute. 'async' by default. */
  decoding?: 'sync' | 'async' | 'auto'
  /** Overrides the fetch priority. Set by `priority` when not given. */
  fetchPriority?: 'high' | 'low' | 'auto'
}

export function CmsImage({
  uuid,
  alt,
  sizes = '100vw',
  width,
  height,
  priority = false,
  loading,
  decoding,
  fetchPriority,
  ...rest
}: CmsImageProps) {
  // The current locale, for the media library's alt fallback. The global
  // i18next instance is initialised before the app renders — including during
  // the build-time prerender (scripts/prerender.tsx awaits `changeLanguage`
  // before importing the app), so this resolves during SSR too.
  const { i18n } = useTranslation()
  const mediaIndex = useCmsMediaIndex()

  const entry = uuid ? mediaIndex[uuid] : undefined
  const src = entry ? cmsMediaLargestWebpUrl(uuid, mediaIndex) : undefined
  // No row, or no WebP rung (a PDF, a video, a row whose variants were never
  // generated): render nothing rather than a broken .
  if (!entry || !src) return null

  return (
    <img
      src={src}
      srcSet={cmsMediaSrcSet(uuid, mediaIndex)}
      sizes={sizes}
      alt={alt ?? mediaAltForLocale(entry, i18n.language)}
      width={width ?? entry.width ?? undefined}
      height={height ?? entry.height ?? undefined}
      loading={loading ?? (priority ? 'eager' : 'lazy')}
      decoding={decoding ?? 'async'}
      fetchPriority={fetchPriority ?? (priority ? 'high' : undefined)}
      {...rest}
    />
  )
}

export default CmsImage
